import { useState } from 'react';

import { ServerErrorYellowSmall } from '@randstad-lean-mobile-factory/react-assets/dist/illustrations';
import {
  Button,
  Checkbox,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  Option,
  WithLightTitle,
} from '@randstad-lean-mobile-factory/react-components-core';
import {
  ErrorMessage,
  Select,
  TextInput,
  useFormWithZodResolver,
} from '@randstad-lean-mobile-factory/react-form-fields';
import { useController } from 'react-hook-form';
import { z } from 'zod';

import { useCreateRoute } from 'src/Hooks/MbapiAdmin/useCreateRoute';
import { useGetAllRoutes } from 'src/Hooks/MbapiAdmin/useGetAllRoutes';
import { RouteMethod } from 'src/Services/API';

import styles from './CreateRouteModal.module.scss';

const schema = z.object({
  method: z.nativeEnum(RouteMethod),
  path: z.string().min(1),
  usernames: z.array(z.string()).min(1),
});

export const CreateRouteModal = () => {
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const { data, isLoading, refetch } = useGetAllRoutes();

  const { control, reset, handleSubmit } = useFormWithZodResolver({
    schema: schema,
    defaultValues: { method: RouteMethod.GET, path: '', usernames: [] },
  });

  const createRoute = useCreateRoute({
    onSuccess: () => {
      setTimeout(() => setIsUserModalOpen(false), 500);
    },
    onSettled: () => {
      setTimeout(() => createRoute.reset(), 500);
    },
  });

  const {
    field,
    fieldState: { error },
  } = useController({
    name: 'usernames',
    control,
  });

  return (
    <>
      <Button
        variant="secondary"
        size="medium"
        onClick={() => {
          reset();
          setIsUserModalOpen(true);
        }}
      >
        créer une route
      </Button>
      <Modal size="medium" open={isUserModalOpen} onClose={() => setIsUserModalOpen(false)}>
        <ModalContent title="créer une route">
          <WithLightTitle title="méthode">
            <Select control={control} name="method" size="medium">
              {Object.keys(RouteMethod).map(method => (
                <Option value={method} key={method}>
                  {method}
                </Option>
              ))}
            </Select>
          </WithLightTitle>
          <WithLightTitle title="route">
            <TextInput control={control} name="path" placeholder="/controller/method" />
          </WithLightTitle>
          <WithLightTitle title="utilisateurs" className={styles.users}>
            {isLoading ? (
              <div className={styles.illustration}>
                <Loader size="small" />
                chargement en cours
              </div>
            ) : !data ? (
              <div className={styles.illustration}>
                <ServerErrorYellowSmall />
                désolé, une erreur est survenue
                <Button size="medium" onClick={() => refetch()}>
                  réessayer
                </Button>
              </div>
            ) : (
              data.users.map(username => (
                <Checkbox
                  checked={field.value.includes(username)}
                  onChange={() =>
                    field.onChange(
                      field.value.includes(username)
                        ? field.value.filter(user => user !== username)
                        : [...field.value, username]
                    )
                  }
                >
                  {username}
                </Checkbox>
              ))
            )}
            <ErrorMessage error={error} />
          </WithLightTitle>
        </ModalContent>
        <ModalActions side="right">
          <Button
            onClick={handleSubmit(values => createRoute.mutateAsync(values))}
            mutationStatus={createRoute.status}
          >
            valider
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};
